// src/components/LoanModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import creFields from '../data/creFields.json';
import config from '../data/config.json';
import { getFieldChoices, lookupZipCode } from '../api';
import { getChoiceDisplayLabel } from '../utils/creUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

// `template`: add mode prefilled from another loan (Duplicate). It still gets a
// freshly generated CUSIP and a null MODIFIED_DATE: it is a new key.
const LoanModal = ({ open, onClose, onSave, propertyType, userId, loanData, template = null, existingLoans = [] }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [dynamicChoices, setDynamicChoices] = useState({});
  const isEditMode = !!loanData;

  // Generate next CUSIP: find the loan with the latest origination date, take its CUSIP + 100
  const generateNextCusip = () => {
    if (!existingLoans || existingLoans.length === 0) {
      return '100';
    }

    try {
      // Find the loan with the latest ORIGINATION_DATE
      let latestDate = null;
      let latestCusipStr = null;

      existingLoans.forEach(loan => {
        if (!loan.ORIGINATION_DATE) return;
        // Skip unsaved temporary rows
        if (String(loan.CUSIP || '').startsWith('new_row_')) return;
        const d = dayjs(loan.ORIGINATION_DATE);
        if (!d.isValid()) return;
        if (!latestDate || d.isAfter(latestDate)) {
          latestDate = d;
          latestCusipStr = loan.CUSIP;
        }
      });

      if (!latestCusipStr) {
        return '100';
      }

      // Strip middle part if in format XXXXX-XXXXXX-XXX
      let latestCusip;
      if (String(latestCusipStr).includes('-')) {
        const parts = String(latestCusipStr).split('-');
        latestCusip = parseInt(parts[1], 10);
      } else {
        latestCusip = parseInt(latestCusipStr, 10);
      }

      if (isNaN(latestCusip)) {
        return '100';
      }

      // Collect all existing numeric CUSIPs to check for duplicates
      const existingCusips = new Set();
      existingLoans.forEach(loan => {
        const c = String(loan.CUSIP || '');
        if (!c || c.startsWith('new_row_')) return;
        let num;
        if (c.includes('-')) {
          num = parseInt(c.split('-')[1], 10);
        } else {
          num = parseInt(c, 10);
        }
        if (!isNaN(num)) existingCusips.add(num);
      });

      // Increment by 100 until we find a CUSIP that doesn't already exist
      let newCusip = latestCusip + 100;
      while (existingCusips.has(newCusip)) {
        newCusip += 100;
      }

      return String(newCusip);
    } catch (error) {
      console.warn('Error generating CUSIP:', error);
      return '100';
    }
  };

  // Get unique categories from creFields and order them
  const categoryOrder = ['Main', 'Loan', 'Property', 'ClearWater', 'AM', 'Accounting', 'Allocation', 'ESG'];
  const uniqueCategories = [...new Set(Object.values(creFields).map(f => f.category).filter(Boolean))];
  const categories = categoryOrder.filter(cat => uniqueCategories.includes(cat));

  // Initialize form data when modal opens
  useEffect(() => {
    if (open) {
      let initialData = {};

      if ((isEditMode && loanData) || template) {
        // Edit mode: use existing data. Duplicate: the template's data under a
        // new generated CUSIP, as a never-saved loan.
        initialData = isEditMode
          ? { ...loanData }
          : { ...template, CUSIP: generateNextCusip(), MODIFIED_DATE: null };
        delete initialData.MODIFIED_BY;
        delete initialData._isUploadedNew;

        // Convert date strings to dayjs objects for date fields
        config.dateFields.forEach(field => {
          const fieldValue = initialData[field];
          if (fieldValue != null && fieldValue !== '' && fieldValue !== undefined) {
            try {
              if (fieldValue && typeof fieldValue.isValid === 'function') {
                initialData[field] = fieldValue.isValid() ? fieldValue : null;
              } else {
                const dateValue = dayjs(fieldValue);
                if (dateValue && typeof dateValue.isValid === 'function' && dateValue.isValid()) {
                  initialData[field] = dateValue;
                } else {
                  initialData[field] = null;
                }
              }
            } catch (error) {
              console.warn(`Error parsing date field ${field}:`, error);
              initialData[field] = null;
            }
          } else {
            initialData[field] = null;
          }
        });

        // Ensure boolean fields are properly set
        config.booleanFields.forEach(field => {
          if (initialData[field] === undefined || initialData[field] === null) {
            initialData[field] = false;
          }
        });
      } else {
        // Add mode: use defaults with generated CUSIP
        const generatedCusip = generateNextCusip();
        initialData = {
          PROPERTY_TYPE: propertyType || '',
          LOAN_NAME: '',
          PROPERTY_NAME: '',
          CUSIP: generatedCusip,
          ENTITY: '',
          LOAN_TYPE: '',
          INTEREST_TYPE: '',
          CURRENCY: 'USD',
          STATUS: 5,
          // The writer stamps both audit fields; null is the version token for
          // a new loan (rather than today's date, which will never match).
          MODIFIED_DATE: null
        };

        // Initialize boolean fields to false
        config.booleanFields.forEach(field => {
          initialData[field] = false;
        });

        // Initialize date fields to null
        config.dateFields.forEach(field => {
          initialData[field] = null;
        });
      }

      setFormData(initialData);
      setErrors({});
      setActiveTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propertyType, userId, loanData, template, isEditMode, existingLoans]);

  // Fetch dynamic field choices when dependent fields change
  useEffect(() => {
    const fetchDynamicChoices = async () => {
      if (formData.PROPERTY_TYPE) {
        try {
          const response = await getFieldChoices('PROPERTY_SUBTYPE', formData.PROPERTY_TYPE);
          setDynamicChoices(prev => ({
            ...prev,
            PROPERTY_SUBTYPE: response.data.choices || []
          }));
        } catch (error) {
          console.error('Failed to fetch Property Subtype choices:', error);
          setDynamicChoices(prev => ({
            ...prev,
            PROPERTY_SUBTYPE: []
          }));
        }
      } else {
        setDynamicChoices(prev => ({
          ...prev,
          PROPERTY_SUBTYPE: []
        }));
      }
    };

    fetchDynamicChoices();
  }, [formData.PROPERTY_TYPE]);

  // Look up geographic data when ZIP_CODE changes (5-digit ZIP)
  useEffect(() => {
    const zip = String(formData.ZIP_CODE || '').trim();
    if (!/^\d{5}$/.test(zip)) return;

    let cancelled = false;
    const fetchZipData = async () => {
      try {
        const response = await lookupZipCode(zip);
        if (cancelled) return;
        const data = response.data;
        setFormData(prev => ({
          ...prev,
          ...(data.CITY && !prev.CITY ? { CITY: data.CITY } : {}),
          ...(data.STATE_ID && !prev.STATE_ID ? { STATE_ID: data.STATE_ID } : {}),
          ...(data.COUNTRY && !prev.COUNTRY ? { COUNTRY: data.COUNTRY } : {}),
          ...(data.CURRENCY && !prev.CURRENCY ? { CURRENCY: data.CURRENCY } : {}),
          ...(data.MSA && !prev.CSA ? { CSA: data.MSA } : {}),
        }));
      } catch (error) {
        // ZIP lookup is best-effort; don't block the user
        console.warn('ZIP lookup failed:', error);
      }
    };
    fetchZipData();
    return () => { cancelled = true; };
  }, [formData.ZIP_CODE]);

  const handleInputChange = (field, value) => {
    const updatedFormData = {
      ...formData,
      [field]: value
    };

    // Handle cascading dropdowns - clear PROPERTY_SUBTYPE when PROPERTY_TYPE changes
    if (field === 'PROPERTY_TYPE') {
      updatedFormData.PROPERTY_SUBTYPE = '';
    }

    setFormData(updatedFormData);

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateField = (field, value) => {
    const requiredFields = config.validation.requiredFields;

    if (requiredFields.includes(field)) {
      if (field === 'CUSIP' && !isEditMode) {
        // CUSIP is not required for new entries
        return '';
      }
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return 'This field is required';
      }
    }

    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    config.validation.requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    // Convert dayjs objects back to strings for date fields
    const dataToSave = { ...formData };

    // Sanitize: convert empty strings and whitespace-only values to null
    Object.keys(dataToSave).forEach(key => {
      if (typeof dataToSave[key] === 'string' && dataToSave[key].trim() === '') {
        dataToSave[key] = null;
      }
    });

    config.dateFields.forEach(field => {
      if (dataToSave[field]) {
        if (dayjs.isDayjs(dataToSave[field]) && dataToSave[field].isValid()) {
          dataToSave[field] = dataToSave[field].format('YYYY-MM-DD');
        } else if (!dayjs.isDayjs(dataToSave[field])) {
          if (typeof dataToSave[field] !== 'string') {
            dataToSave[field] = null;
          }
        } else {
          dataToSave[field] = null;
        }
      } else {
        dataToSave[field] = null;
      }
    });

    // Convert boolean fields
    config.booleanFields.forEach(field => {
      if (dataToSave[field] === undefined) {
        dataToSave[field] = false;
      }
    });

    // The writer stamps MODIFIED_BY and the new MODIFIED_DATE. Keep the exact
    // token from the loaded row for edits, or null for a new loan. Never turn
    // this into a UI-selected calendar date or restamp it locally.
    dataToSave.MODIFIED_DATE = isEditMode ? (loanData?.MODIFIED_DATE ?? null) : null;
    delete dataToSave.MODIFIED_BY;

    // Generate a temporary CUSIP if not provided
    if (!dataToSave.CUSIP || dataToSave.CUSIP.trim() === '') {
      dataToSave.CUSIP = `new_row_${Date.now()}`;
    }

    onSave(dataToSave);
    onClose();
  };

  const renderField = (fieldKey, fieldConfig) => {
    const value = formData[fieldKey] !== undefined ? formData[fieldKey] : '';
    const error = errors[fieldKey] || '';

    // System fields cannot be edited; the loaded MODIFIED_DATE is the writer's token.
    // Skip CUSIP since it's shown in the special section at the top of Main tab
    if (['MODIFIED_BY', 'MODIFIED_DATE', 'EDIT', 'CUSIP'].includes(fieldKey)) {
      return null;
    }

    const isRequired = config.validation.requiredFields.includes(fieldKey);
    // CUSIP is read-only in both add mode (auto-generated) and edit mode
    const isReadOnly = !fieldConfig.editable || fieldKey === 'CUSIP';

    const commonProps = {
      fullWidth: true,
      value: value || '',
      error: !!error,
      helperText: error,
      size: 'small',
      InputLabelProps: { shrink: true },
      // Use non-breaking spaces to maintain width without cluttering with duplicate text
      placeholder: '\u00A0'.repeat(fieldConfig.name.length)
    };

    // Boolean fields (Checkboxes)
    if (fieldConfig.datatype === 'boolean') {
      return (
        <FormControlLabel
          key={fieldKey}
          control={
            <Checkbox
              checked={value === true || value === 'Y' || value === 1}
              onChange={(e) => handleInputChange(fieldKey, e.target.checked)}
              disabled={isReadOnly}
            />
          }
          label={fieldConfig.name}
        />
      );
    }

    // Date fields
    if (fieldConfig.datatype === 'date') {
      let dateValue = null;
      if (value != null && value !== '' && value !== undefined) {
        try {
          if (value && typeof value.isValid === 'function') {
            dateValue = value.isValid() ? value : null;
          } else {
            const parsedDate = dayjs(value);
            if (parsedDate && typeof parsedDate.isValid === 'function') {
              dateValue = parsedDate.isValid() ? parsedDate : null;
            }
          }
        } catch (error) {
          dateValue = null;
        }
      }

      // Destructure to exclude 'value' from commonProps - DatePicker manages its own value
      const { value: _ignoredValue, ...textFieldProps } = commonProps;

      return (
        <DatePicker
          key={fieldKey}
          label={fieldConfig.name}
          value={dateValue}
          onChange={(newValue) => handleInputChange(fieldKey, newValue)}
          format="YYYY-MM-DD"
          disabled={isReadOnly}
          slotProps={{
            textField: {
              ...textFieldProps,
              fullWidth: true,
              size: 'small',
              required: isRequired,
              InputLabelProps: { shrink: true }
            }
          }}
        />
      );
    }

    // Dropdown fields with choices
    if (fieldConfig.choices) {
      let choices = [];

      // Use dynamic choices from API if available (e.g., PROPERTY_SUBTYPE)
      if (dynamicChoices[fieldKey]) {
        choices = dynamicChoices[fieldKey];
      }
      // Handle cascading dropdowns from static config (fallback)
      else if (typeof fieldConfig.choices === 'object' && !Array.isArray(fieldConfig.choices)) {
        const parentValue = formData.PROPERTY_TYPE;
        if (parentValue && fieldConfig.choices[parentValue]) {
          choices = fieldConfig.choices[parentValue];
        }
      } else {
        choices = fieldConfig.choices;
      }

      return (
        <FormControl fullWidth key={fieldKey} size="small" error={!!error} required={isRequired}
          sx={{ minWidth: `${fieldConfig.name.length + 4}ch` }}
        >
          <InputLabel shrink sx={{ maxWidth: 'none' }}>{fieldConfig.name}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            label={fieldConfig.name}
            disabled={isReadOnly}
            notched
            displayEmpty
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {choices.map((choice) => (
              <MenuItem key={choice} value={choice}>
                {getChoiceDisplayLabel(fieldKey, choice)}
              </MenuItem>
            ))}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }

    // Numeric fields
    if (fieldConfig.datatype === 'numeric') {
      let inputValue = value;
      let step = 1;

      if (fieldConfig.subtype === 'percent') {
        // Convert decimal to percentage for display
        if (value && !isNaN(parseFloat(value))) {
          inputValue = (parseFloat(value) * 100).toFixed(2);
        }
        step = 0.01;
      } else if (fieldConfig.subtype === 'int') {
        step = 1;
      } else {
        step = 0.01;
      }

      return (
        <TextField
          key={fieldKey}
          {...commonProps}
          label={fieldConfig.name}
          type="number"
          required={isRequired}
          disabled={isReadOnly}
          value={inputValue}
          onChange={(e) => {
            let val = e.target.value;
            if (fieldConfig.subtype === 'percent' && val !== '') {
              val = parseFloat(val) / 100;
            }
            handleInputChange(fieldKey, val);
          }}
          inputProps={{
            step: step
          }}
          InputProps={{
            endAdornment: fieldConfig.subtype === 'percent' ? '%' : null
          }}
        />
      );
    }

    // Text fields
    return (
      <TextField
        key={fieldKey}
        {...commonProps}
        label={fieldConfig.name}
        required={isRequired}
        disabled={isReadOnly}
        onChange={(e) => handleInputChange(fieldKey, e.target.value)}
        multiline={fieldKey.includes('NOTES') || fieldKey.includes('COMMENTS') || fieldKey.includes('DESCRIPTION')}
        rows={fieldKey.includes('NOTES') || fieldKey.includes('COMMENTS') || fieldKey.includes('DESCRIPTION') ? 3 : 1}
      />
    );
  };

  const getFieldsByCategory = (category) => {
    return Object.entries(creFields)
      .filter(([key, field]) => field.category === category && key !== 'EDIT')
      .map(([key, field]) => ({ key, ...field }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {isEditMode
                ? `Edit CRE Loan - ${loanData?.LOAN_NAME || loanData?.CUSIP || 'Entry'}`
                : `Add New CRE Loan - ${propertyType || 'Unknown Type'}`
              }
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            {categories.map((category, idx) => (
              <Tab key={category} label={category} />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {categories.map((category, idx) => (
              <Box
                key={category}
                role="tabpanel"
                hidden={activeTab !== idx}
              >
                {activeTab === idx && (
                  <>
                    {/* Show CUSIP and Modified Date section on Main tab */}
                    {category === 'Main' && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        {/* CUSIP field - shown for both add and edit modes */}
                        <Grid container spacing={2} alignItems="center">
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="CUSIP"
                              value={formData.CUSIP || ''}
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                        {/* Read-only audit timestamp. The writer sets this on save;
                            an edit must send the loaded instant, unchanged. */}
                        <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Modified Date"
                              value={formData.MODIFIED_DATE || 'New loan — not yet saved'}
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      {getFieldsByCategory(category).map(({ key, ...fieldConfig }) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={key}>
                          {renderField(key, fieldConfig)}
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            {isEditMode ? 'Save Changes' : 'Create Loan'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default LoanModal;
